import { useEffect } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Close } from 'blaze-ui/icons'
import { FontPairingCard, FontPairingCardSkeleton } from 'components/FontPairingCard'
import { FontPairingsModal } from 'components/FontPairingsModal'
import { FontsModal } from 'components/FontsModal/FontsModal'
import { VisualStylePreview } from 'components/VisualStylePreview/VisualStylePreview'
import { StyleGenerated } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection/GenerationStage'
import { useFontPairingSelection } from '../hooks'
import Styles from '../VisualStylesForExistingUsersModal.module.scss'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'

interface FontPairingSelectionStepProps {
  selectedFontPairingId: number | null
  onSelectFontPairing: (fontPairingId: number) => void
  onNext: () => void
  onBack: () => void
  onClose: () => void
  isLoading?: boolean
  recommendedIds?: number[]
  selectedStyle: StyleGenerated | null
  brandColor?: string
}

export const FontPairingSelectionStep = ({
  selectedFontPairingId,
  onSelectFontPairing,
  onNext,
  onBack,
  onClose,
  isLoading: isLoadingRecommendations = false,
  recommendedIds,
  selectedStyle,
  brandColor,
}: FontPairingSelectionStepProps) => {
  const { openModal } = useModals()

  const {
    fontPairingsData,
    displayedFontPairings,
    customFonts,
    hasCustomFonts,
    showLoading,
    didManuallyChangeFontPairing,
    handleSelectFontPairing,
  } = useFontPairingSelection({
    selectedFontPairingId,
    onSelectFontPairing,
    recommendedIds,
    isLoadingRecommendations,
    skipAutoSelectIfCustomFonts: true,
  })

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-font-pairing-step',
      has_custom_fonts: hasCustomFonts,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenFontPairingsModal = () => {
    openModal(
      FontPairingsModal,
      {
        selectedFontPairingId,
        onConfirm: handleSelectFontPairing,
      },
      { newStack: true },
    )
  }

  const handleOpenUploadFontsModal = () => {
    openModal(FontsModal, undefined, { newStack: true })
  }

  const handleNext = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-font-pairing-step',
      font_pairing_id: selectedFontPairingId ?? undefined,
      recommended_font_pairing_ids: recommendedIds,
      did_manually_change_font_pairing: didManuallyChangeFontPairing,
      has_custom_fonts: hasCustomFonts,
    })
    onNext()
  }

  return (
    <Modal.Content className={Styles.fontPairingContent}>
      <div className={Styles.fontPairingStep}>
        <div className={Styles.fontPairingLeftPanel}>
          <div className={Styles.fontPairingLeftPanelContent}>
            <div className={Styles.fontPairingHeader}>
              <Heading level={1}>Select a brand font</Heading>
              <Text color="var(--dark-40)" variant="primary">
                Based on your brand, we recommend:
              </Text>
            </div>

            <div className={Styles.fontPairingCards}>
              {showLoading && [1, 2, 3].map((i) => <FontPairingCardSkeleton key={i} />)}
              {!showLoading && hasCustomFonts && (
                <div className={Styles.customFontCard}>
                  <Text variant="primary" color="var(--dark-80)">
                    Custom Font Uploaded
                  </Text>
                  <Text variant="label" color="var(--dark-40)">
                    Your uploaded fonts will be used for social posts
                  </Text>
                </div>
              )}
              {!showLoading &&
                !hasCustomFonts &&
                displayedFontPairings.map((fontPairing) => (
                  <FontPairingCard
                    key={fontPairing.id}
                    fontPairing={fontPairing}
                    isSelected={selectedFontPairingId === fontPairing.id}
                    onSelect={() => handleSelectFontPairing(fontPairing.id)}
                  />
                ))}
            </div>

            <div className={Styles.fontPairingActions}>
              <Button variant="secondary" size="md" onPress={handleOpenUploadFontsModal}>
                Upload Your Own
              </Button>
              <Button variant="secondary" size="md" onPress={handleOpenFontPairingsModal}>
                Browse all fonts
              </Button>
            </div>
          </div>

          <div className={Styles.leftPanelFooter}>
            <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
              Back
            </Button>
            <Button
              variant="primary"
              size="lg"
              onPress={handleNext}
              isDisabled={!selectedFontPairingId && !hasCustomFonts}
              aria-label="Looks Good!"
            >
              Looks Good!
            </Button>
          </div>
        </div>
        <div className={Styles.fontPairingRightPanel}>
          <Button size="sm" variant="tertiary" onPress={onClose} className={Styles.stepCloseButton}>
            <Close color="var(--light-100)" />
          </Button>
          {selectedStyle && fontPairingsData && (
            <VisualStylePreview
              fontPairingId={selectedFontPairingId}
              styleGenerated={selectedStyle}
              brandColor={brandColor}
              fontPairings={fontPairingsData}
              firstCustomFont={customFonts[0]}
            />
          )}
        </div>
      </div>
    </Modal.Content>
  )
}
